import { get } from "lodash";
import { useEffect, useState } from "react";
import { useLazyQuery, useMutation, useQuery, gql } from "@apollo/client";
import { FormProvider, useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { QUERY_SEARCH_ANYWHERE } from "../query";
import classNames from "classnames";
import { useAuth, useAuthContext } from "../../../context/AuthContext";
import { Persona } from "../../../elements/Persona";
import {
  CLAIM_ORGANISATION_USER,
  CREATE_ORGANIZATION,
} from "../../../queries/fundingkit";
import { InputBaseText, InputDropdownSingle } from "../inputs";
import { isBriterBridgesUser } from "../../../utils/strings";

export const StepClaimOrganization = ({ step, onSuccess }: any) => {
  const [hasClaimed, setHasClaimed] = useState<boolean>(false);
  const [claimErrorMsg, setClaimErrorMsg] = useState<string | null>(null);
  const { user, getUser } = useAuthContext();
  const [claimWithUser, { loading: claimLoading }] = useMutation(
    CLAIM_ORGANISATION_USER,
  );

  const handleClaim = (company_id: string) => {
    setClaimErrorMsg(null);
    claimWithUser({
      variables: {
        user_id: user?.id,
        organization_id: company_id,
      },
    })
      .then(() => {
        setHasClaimed(true);
      })
      .catch((err) => {
        setClaimErrorMsg(
          err.message ||
            "Failed to claim organization. Please try again or contact support@briterbridges.com",
        );
      });
  };

  if (hasClaimed) {
    return (
      <div className="w-full flex flex-col gap-12 justify-center items-center py-8">
        <div className="w-full text-center">
          <p className="font-semibold text-2xl">
            Thank you! We'll be in touch soon to verify your information
          </p>
          <p className="text-gray-500 text-sm py-2">
            Please keep an eye on your email inbox, as we'll be sending updates
            and next steps there once your request has been reviewed
          </p>
        </div>
        <svg
          width="190"
          height="171"
          viewBox="0 0 190 171"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="text-uw-primary"
        >
          <g clip-path="url(#clip0_4_334)">
            <path
              d="M188.333 65.3939C188.265 65.3939 188.199 65.373 188.143 65.334L95.9566 0.966525C95.6761 0.771555 95.3427 0.667359 95.0013 0.667943C94.66 0.668526 94.327 0.77386 94.0471 0.969788L2.52485 65.3333C2.45246 65.3842 2.36286 65.4042 2.27577 65.3889C2.18868 65.3736 2.11122 65.3242 2.06045 65.2516C2.00968 65.179 1.98974 65.0892 2.00502 65.0019C2.0203 64.9146 2.06956 64.8369 2.14195 64.786L93.6643 0.422476C94.0562 0.148218 94.5224 0.000790583 95.0003 3.17072e-06C95.4782 -0.000784241 95.9448 0.145105 96.3376 0.41807L188.524 64.7853C188.582 64.826 188.626 64.8843 188.649 64.9516C188.672 65.019 188.673 65.0919 188.651 65.1598C188.63 65.2277 188.588 65.287 188.531 65.3291C188.473 65.3712 188.404 65.3939 188.333 65.3939Z"
              fill="#3F3D56"
            />
            <path
              d="M7.75464 67.6755L95.092 2.78015L183.092 72.293L99.592 121.921L54.2586 111.561L7.75464 67.6755Z"
              fill="#E6E6E6"
            />
            <path
              d="M58.0852 152.574H14.9384C14.6735 152.575 14.4111 152.523 14.1662 152.421C13.9214 152.32 13.6988 152.171 13.5114 151.983C13.3239 151.796 13.1752 151.573 13.0737 151.327C12.9723 151.082 12.92 150.819 12.92 150.553C12.92 150.288 12.9723 150.025 13.0737 149.779C13.1752 149.534 13.3239 149.311 13.5114 149.123C13.6988 148.936 13.9214 148.787 14.1662 148.686C14.4111 148.584 14.6735 148.532 14.9384 148.532H58.0852C58.3501 148.532 58.6125 148.584 58.8574 148.686C59.1023 148.787 59.3248 148.936 59.5122 149.123C59.6997 149.311 59.8484 149.534 59.9499 149.779C60.0514 150.025 60.1036 150.288 60.1036 150.553C60.1036 150.819 60.0514 151.082 59.9499 151.327C59.8484 151.573 59.6997 151.796 59.5122 151.983C59.3248 152.171 59.1023 152.32 58.8574 152.421C58.6125 152.523 58.3501 152.575 58.0852 152.574Z"
              fill="currentColor"
            />
            <path
              d="M30.4185 143.885H14.9384C14.6735 143.886 14.4111 143.834 14.1662 143.732C13.9214 143.631 13.6988 143.482 13.5114 143.294C13.3239 143.107 13.1752 142.884 13.0737 142.638C12.9723 142.393 12.92 142.13 12.92 141.864C12.92 141.599 12.9723 141.336 13.0737 141.09C13.1752 140.845 13.3239 140.622 13.5114 140.434C13.6988 140.247 13.9214 140.098 14.1662 139.996C14.4111 139.895 14.6735 139.843 14.9384 139.843H30.4185C30.6835 139.843 30.9459 139.895 31.1907 139.996C31.4356 140.098 31.6581 140.247 31.8456 140.434C32.033 140.622 32.1818 140.845 32.2832 141.09C32.3847 141.336 32.4369 141.599 32.4369 141.864C32.4369 142.13 32.3847 142.393 32.2832 142.638C32.1818 142.884 32.033 143.107 31.8456 143.294C31.6581 143.482 31.4356 143.631 31.1907 143.732C30.9459 143.834 30.6835 143.886 30.4185 143.885Z"
              fill="currentColor"
            />
            <path
              d="M96.3201 103.821C95.9922 103.822 95.6673 103.758 95.3637 103.633L41.5001 81.226V11.0868C41.5008 10.4666 41.7469 9.87198 42.1843 9.43342C42.6218 8.99486 43.2148 8.74816 43.8335 8.74744H147.167C147.785 8.74816 148.378 8.99486 148.816 9.43342C149.253 9.87198 149.499 10.4666 149.5 11.0868V81.275L149.399 81.3184L97.3055 103.623C96.9935 103.754 96.6585 103.822 96.3201 103.821Z"
              fill="white"
            />
            <path
              d="M96.32 103.988C95.9703 103.989 95.6239 103.921 95.3002 103.788L41.3334 81.3376V11.0868C41.3341 10.4223 41.5978 9.78518 42.0664 9.31528C42.5351 8.84539 43.1706 8.58108 43.8334 8.58032H147.167C147.83 8.58108 148.465 8.84539 148.934 9.31528C149.402 9.78518 149.666 10.4223 149.667 11.0868V81.3853L97.3708 103.776C97.0382 103.916 96.6809 103.989 96.32 103.988ZM42 80.8911L95.5544 103.17C96.0539 103.373 96.6133 103.37 97.1104 103.161L149 80.944V11.0868C148.999 10.5995 148.806 10.1323 148.462 9.78772C148.119 9.44313 147.653 9.24929 147.167 9.24872H43.8334C43.3473 9.24929 42.8814 9.44313 42.5377 9.78772C42.194 10.1323 42.0006 10.5995 42.0001 11.0868L42 80.8911Z"
              fill="#3F3D56"
            />
            <path
              d="M187.667 64.7255H187.6L149.333 81.1078L96.9767 103.522C96.7721 103.609 96.5526 103.654 96.3307 103.655C96.1087 103.656 95.8887 103.613 95.6833 103.529L41.6667 81.061L2.45996 64.7522L2.40007 64.7255H2.33333C1.71471 64.7262 1.12161 64.9728 0.684174 65.4114C0.246737 65.85 0.000686417 66.4446 0 67.0648V168.661C0.000687297 169.281 0.246738 169.875 0.684174 170.314C1.12161 170.753 1.71471 170.999 2.33333 171H187.667C188.285 170.999 188.878 170.753 189.316 170.314C189.753 169.875 189.999 169.281 190 168.661V67.0648C189.999 66.4446 189.753 65.85 189.316 65.4114C188.878 64.9728 188.285 64.7262 187.667 64.7255ZM189.333 168.661C189.333 169.104 189.158 169.529 188.845 169.842C188.532 170.155 188.109 170.331 187.667 170.332H2.33333C1.89136 170.331 1.46754 170.155 1.15502 169.842C0.842497 169.529 0.666843 169.104 0.666667 168.661V67.0648C0.667271 66.6327 0.834454 66.2176 1.13326 65.9062C1.43207 65.5947 1.83937 65.4112 2.27002 65.3939L41.6667 81.7829L95.4268 104.147C96.0087 104.385 96.6608 104.381 97.2401 104.137L149.333 81.833L187.733 65.3939C188.163 65.4125 188.57 65.5965 188.867 65.9078C189.165 66.219 189.332 66.6334 189.333 67.0648V168.661Z"
              fill="#3F3D56"
            />
            <path
              d="M95.7817 83.9325C93.7932 83.9363 91.8574 83.2916 90.2664 82.0957L90.1678 82.0215L69.3974 66.0778C68.4355 65.3389 67.6282 64.4172 67.0215 63.3655C66.4149 62.3137 66.0208 61.1525 65.8618 59.948C65.7028 58.7436 65.7819 57.5195 66.0947 56.3458C66.4075 55.172 66.9479 54.0715 67.6849 53.1071C68.4219 52.1427 69.3412 51.3333 70.3902 50.725C71.4393 50.1168 72.5975 49.7217 73.7988 49.5623C75.0002 49.4028 76.221 49.4822 77.3918 49.7958C78.5625 50.1094 79.6602 50.6512 80.6221 51.3901L94.0756 61.7328L125.868 20.1631C126.606 19.199 127.525 18.3899 128.574 17.782C129.624 17.1742 130.782 16.7795 131.983 16.6205C133.185 16.4615 134.406 16.5413 135.576 16.8553C136.747 17.1694 137.844 17.7116 138.806 18.4509L138.608 18.72L138.811 18.4548C140.751 19.9495 142.02 22.1546 142.34 24.5864C142.66 27.0182 142.005 29.4779 140.519 31.4261L103.123 80.322C102.258 81.4487 101.146 82.3605 99.8729 82.9867C98.5997 83.6128 97.1998 83.9365 95.7817 83.9325Z"
              fill="currentColor"
            />
          </g>
          <defs>
            <clipPath id="clip0_4_334">
              <rect width="190" height="171" fill="white" />
            </clipPath>
          </defs>
        </svg>
        <button
          className="bg-uw-primary px-4 py-1 rounded-md text-uw-text-primary"
          onClick={onSuccess}
        >
          Continue while waiting
        </button>
      </div>
    );
  }

  return (
    <SelectOrCreateOrganization
      onClaim={handleClaim}
      claimLoading={claimLoading}
      claimError={claimErrorMsg}
      onSuccess={onSuccess}
    />
  );
};

const CreateOrganizationForm = ({
  onCreateSuccess,
  onCancel,
  initialName,
}: any) => {
  const methods = useForm({
    defaultValues: {
      name: initialName || "",
      description: "",
      country: null,
      office_country: null,
      office_address: "",
    },
  });

  const [createOrg, { loading: createLoading }] =
    useMutation(CREATE_ORGANIZATION);
  const [createError, setCreateError] = useState<string | null>(null);

  const { data: countryData } = useQuery(
    gql`
      query getCountries($search: SearchRequest!) {
        countries: search(search: $search) {
          items
          total
        }
      }
    `,
    {
      variables: {
        search: {
          bucket: "countries",
          size: 500,
          sort: [{ key: "name.keyword", dir: "asc" }],
          query: {},
        },
      },
    },
  );

  const countryOptions = get(countryData, "countries.items", []).map(
    (c: any) => ({
      id: c.id,
      label: c.name,
    }),
  );

  const onSubmit = (formData: any) => {
    setCreateError(null);
    createOrg({
      variables: {
        input: {
          name: formData.name,
          description: formData.description || "",
          operating_status: "y",
          enabled: false,
          type: "startup",
        },
      },
    })
      .then((result) => {
        const newOrgId = get(result, "data.createOrganization.organization.id");
        if (newOrgId) {
          onCreateSuccess(newOrgId);
        } else {
          setCreateError("Failed to create organization");
        }
      })
      .catch((err) => {
        setCreateError(
          err.message || "Failed to create organization. Please try again.",
        );
      });
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit)}
        className="flex flex-col gap-4 py-8"
      >
        <p className="text-2xl font-semibold block">Create new organisation</p>

        {createError && (
          <div className="bg-red-50 border border-red-400 text-red-800 px-4 py-2 rounded-md text-sm">
            {createError}
          </div>
        )}

        <InputBaseText
          label="Organization Name *"
          type="text"
          {...methods.register("name", { required: true })}
        />

        <div className="flex flex-col gap-2">
          <label className="text-base text-gray font-medium">Description</label>
          <textarea
            {...methods.register("description")}
            rows={3}
            className="w-full px-2 py-1 border border-gray-2000 rounded-md focus:outline-ab-lightgreen focus:border-gray-2100"
            placeholder="Brief description of your organization..."
          />
        </div>

        <InputDropdownSingle
          name="country"
          label="Country of Operation *"
          options={countryOptions}
        />

        <div className="flex flex-col gap-3">
          <p className="text-base text-gray font-medium">Office Address</p>
          <InputDropdownSingle
            name="office_country"
            label="Country"
            options={countryOptions}
          />
          <InputBaseText
            label="Address (City/Street)"
            type="text"
            placeholder="e.g., Nairobi or 123 Main St, Accra"
            {...methods.register("office_address")}
          />
        </div>

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={createLoading}
            className="bg-uw-primary px-4 py-2 rounded-md text-uw-text-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {createLoading ? "Creating..." : "Create & Claim"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="border border-gray-300 px-4 py-2 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Back to search
          </button>
        </div>
      </form>
    </FormProvider>
  );
};

const SelectOrCreateOrganization = ({
  onClaim,
  claimLoading,
  claimError,
  onSuccess,
}: any) => {
  const { session } = useAuth();
  const { user } = useAuthContext();

  const [searchInput, setSearchInput] = useState(
    get(session, "user.organization_raw", "") as string,
  );
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  const [search, { data }] = useLazyQuery(QUERY_SEARCH_ANYWHERE);

  const handleCreateSuccess = (newOrgId: string) => {
    // Automatically claim the newly created organization
    onClaim(newOrgId);
  };

  // Get user's existing claims
  const userClaims = get(user, "claims", []);

  // Check if user is Briter staff
  const isBriterStaff = isBriterBridgesUser(user?.email);

  // Helper function to check claim status for an organization
  const getClaimStatus = (orgId: string) => {
    const claim = userClaims.find((c: any) => c.organization?.id === orgId);
    return claim ? claim.status : null;
  };

  // Check if user has any approved or pending claims (can skip if true)
  const hasValidClaim = userClaims.some(
    (c: any) => c.status === 0 || c.status === 1,
  );

  // Get claimed organizations (pending or approved)
  const claimedOrgs = userClaims
    .filter((c: any) => c.status === 0 || c.status === 1)
    .map((c: any) => c.organization);

  useEffect(() => {
    if (searchInput.length < 2) return;
    search({
      variables: {
        search: {
          bucket: "organizations_list",
          size: 10,
          query: {
            must: [
              {
                bool: {
                  should: [
                    {
                      term: {
                        "name.keyword": searchInput,
                        // "boost": 5
                      },
                    },
                    {
                      match: {
                        name: {
                          query: searchInput,
                          fuzziness: "AUTO:2,10",
                          // "boost": 1
                        },
                      },
                    },
                  ],
                },
              },
            ],
          },
        },
      },
    });
  }, [searchInput]);

  if (showCreateForm) {
    return (
      <CreateOrganizationForm
        onCreateSuccess={handleCreateSuccess}
        onCancel={() => setShowCreateForm(false)}
        initialName={searchInput}
      />
    );
  }

  return (
    <div className="flex w-full flex-col gap-6 py-8">
      {/* Claimed Organizations Section */}
      {claimedOrgs.length > 0 && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <p className="text-2xl font-semibold">
              Request for Managing Organisations
            </p>
            <motion.button
              onClick={() => onSuccess()}
              className="bg-uw-primary px-5 py-1 rounded-md text-uw-text-primary font-medium"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
            >
              Continue
            </motion.button>
          </div>

          <p>
            You've already requested to manage these organizations. Our team is
            reviewing your request and you'll be notified once approved. <br />{" "}
            If you're experiencing delays, please contact us at{" "}
            <a href="mailto:support@briterbridges.com" className="underline">
              support@briterbridges.com
            </a>
            .
          </p>
          <table className="w-full">
            <tbody>
              {claimedOrgs.map((org: any) => {
                const claim = userClaims.find(
                  (c: any) => c.organization?.id === org.id,
                );
                const countries = org?.offices;
                const office = countries
                  ? countries.find((o: any) => o.is_hq_primary)
                  : null;
                return (
                  <tr
                    key={org.id}
                    className="items-center gap-4 border-b first:border-t text-sm"
                  >
                    <td className="text-center rounded w-10">
                      <Persona imageURL={org.profile_image_url} text="" />
                    </td>
                    <td className="text-left rounded">{org.name}</td>
                    <td className="text-sm p-4">
                      <OperatingStatus value={org.operating_status} />
                    </td>
                    <td className="text-nowrap p-2">
                      {org?.sectors?.slice(0, 5)?.map((s: any) => s.name).join(", ")}
                      {org?.sectors && org.sectors.length > 5 && (
                        <span className="ml-1 text-gray-500">+{org.sectors.length - 5}</span>
                      )}
                    </td>
                    <td className="text-nowrap px-4">
                      {office ? `${office.country?.name}` : ""}
                    </td>
                    <td className="text-right">
                      {claim?.status === 0 && (
                        <span className="px-4 py-1 rounded-md text-sm bg-yellow-100 text-yellow-800 border border-yellow-300">
                          Pending
                        </span>
                      )}
                      {claim?.status === 1 && (
                        <span className="px-4 py-1 rounded-md text-sm bg-green-100 text-green-800 border border-green-300">
                          Claimed
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {isBriterStaff && !showSearch && (
            <button
              onClick={() => setShowSearch(true)}
              className="text-sm text-gray-600 hover:text-gray-900 underline w-fit"
            >
              Search for a different organization
            </button>
          )}
        </div>
      )}

      {/* Search Section - Show if no claims OR Briter staff wants to search */}
      {(claimedOrgs.length === 0 || (isBriterStaff && showSearch)) && (
        <>
          <div className="flex items-center justify-between">
            <p className="text-2xl font-semibold">
              {claimedOrgs.length > 0
                ? "Search for Another Organization"
                : "Select an organisation"}
            </p>
            {hasValidClaim && claimedOrgs.length === 0 && (
              <motion.button
                onClick={() => onSuccess()}
                className="bg-gray-200 px-6 py-2 rounded-md text-gray-700 hover:bg-gray-300 font-medium"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                Skip this step
              </motion.button>
            )}
          </div>
          <p>
            To access all features, you'll need to claim a company profile and
            wait for our approval. Start by searching for your company in the
            list and selecting it. If it's not listed, simply click the button
            to create a new one. Once your claim is approved, you'll unlock full
            access.
          </p>
          {get(session, "user.organization_raw", undefined) && (
            <p>
              Searching for your organisation{" "}
              {get(session, "user.organization_raw", undefined)}
            </p>
          )}
          <input
            type="text"
            value={searchInput}
            className="w-full border rounded-md text-base bg-gray-50 py-1.5 px-3 outline-uw-primary"
            onChange={(ev) => {
              setSearchInput(ev.target.value);
            }}
            placeholder="Search your organization and send a request for  claim"
          />
          {claimError && (
            <div className="bg-red-50 border border-red-400 text-red-800 px-4 py-2 rounded-md my-2 text-sm">
              {claimError}
            </div>
          )}
          <table className="w-full">
            <tbody>
              {get(data, "search.items", []).map((s: any) => {
                const countries = s?.offices;
                const office = countries
                  ? countries.find((s: any) => s.is_hq_primary)
                  : null;
                return (
                  <tr
                    key={s.id}
                    className="items-center gap-4 border-b first:border-t text-sm"
                  >
                    <td className="text-center rounded w-10">
                      <Persona imageURL={s.profile_image_url} text="" />
                    </td>
                    <td className="text-left rounded">{s.name}</td>
                    <td className="text-sm p-4">
                      <OperatingStatus value={s.operating_status} />
                    </td>
                    <td className="text-nowrap p-2">
                      {s?.sectors?.slice(0, 5)?.map((s: any) => s.name).join(", ")}
                      {s?.sectors && s.sectors.length > 5 && (
                        <span className="ml-1 text-gray-500">+{s.sectors.length - 5}</span>
                      )}
                    </td>
                    <td className="text-nowrap px-4">
                      {office ? `${office.country?.name}` : ""}
                    </td>
                    <td className="text-right">
                      {(() => {
                        const claimStatus = getClaimStatus(s.id);
                        if (claimStatus === 0) {
                          return (
                            <span className="px-4 py-1 rounded-md text-sm bg-yellow-100 text-yellow-800 border border-yellow-300">
                              Pending
                            </span>
                          );
                        }

                        if (claimStatus === 1) {
                          return (
                            <span className="px-4 py-1 rounded-md text-sm bg-green-100 text-green-800 border border-green-300">
                              Claimed
                            </span>
                          );
                        }

                        if (claimStatus === -1) {
                          return (
                            <span className="px-4 py-1 rounded-md text-sm bg-red-100 text-red-800 border border-red-300">
                              Rejected
                            </span>
                          );
                        }

                        return (
                          <button
                            className={classNames({
                              "bg-uw-primary px-4 py-1 rounded-md text-sm text-uw-text-primary": true,
                              "opacity-50 cursor-not-allowed": claimLoading,
                            })}
                            onClick={(ev) => {
                              ev.preventDefault();
                              onClaim(s.id);
                            }}
                            disabled={claimLoading}
                          >
                            {claimLoading ? "Claiming..." : "Claim"}
                          </button>
                        );
                      })()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {searchInput.length > 2 && (
            <div className="w-full py-8 flex flex-col gap-2.5">
              <div className="flex gap-5 items-center">
                <p className="text-gray-700">Can't find your organisation?</p>
                {/* <motion.button
                  onClick={() => setShowCreateForm(true)}
                  className="bg-uw-primary px-6 py-0.5 rounded-md text-uw-text-primary font-light w-fit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  Create a new organisation
                </motion.button> */}
              </div>
              <p className="text-sm text-gray-600">
                Or get in touch with our{" "}
                <a
                  href="mailto:support@briterbridges.com"
                  className="underline"
                >
                  support@briterbridges.com
                </a>
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const OperatingStatus = ({ value }: any) => {
  const cls = classNames({
    "self-center px-2 py-1 text-xs rounded-md": true,
  });

  switch (value) {
    case "y":
      return (
        <div className="flex-grow justify-start items-center flex">
          <span className="bg-gray-100 px-2 py-0.5 rounded-md">ACTIVE</span>
        </div>
      );
    case "n":
      return (
        <div className="flex-grow justify-start items-center flex">
          <span className="bg-gray-100 px-2 py-0.5 rounded-md">SHUT DOWN</span>
        </div>
      );
    case "c":
      return (
        <div className="flex-grow justify-start items-center flex">
          <span className="bg-gray-100 px-2 py-0.5 rounded-md">
            UNDER REVIEW
          </span>
        </div>
      );
    default:
      return <span className={cls}>{value?.operating_status}</span>;
  }
};
